import { useParams, Navigate } from 'react-router-dom'
import { DocPage } from '@/components/DocPage'
import { ContentRenderer } from '@/components/ContentRenderer'
import { SEO } from '@/components/SEO'
import { getFaqData } from '@/lib/navigation'
import { useLocale } from '@/i18n'

interface Question {
  slug: string
  title: string
  description: string
  content: Array<{
    type: 'p' | 'h2' | 'h3' | 'ul' | 'ol' | 'table' | 'callout' | 'code'
    text?: string
    items?: string[]
    headers?: string[]
    rows?: string[][]
    variant?: 'info' | 'warning' | 'success' | 'error'
    title?: string
    language?: string
  }>
  keyTakeaways?: string[]
}

interface Section {
  id: string
  title: string
  questions: Question[]
}

interface FAQData {
  sections: Section[]
}

// Map section IDs to relevant keywords for SEO
const sectionKeywords: Record<string, string[]> = {
  'vision-product-market': ['RexHook vision', 'Uniswap V4 hooks', 'token launch platform', 'DeFi infrastructure'],
  'technical': ['smart contracts', 'V4 hooks', 'DynamicFeeHook', 'MEV protection', 'gas optimization'],
  'team-trust': ['RexHook team', 'blockchain developers', 'DeFi security'],
  'revenue': ['protocol revenue', 'fee structure', 'subscriptions', 'certification fees'],
  'participation': ['go-to-market', 'roadmap', 'mainnet launch']
}

export function FAQPage() {
  const { section, slug } = useParams<{ section: string; slug: string }>()
  const { locale, localizeHref } = useLocale()

  // Find the question in the FAQ data
  const faq = getFaqData(locale) as FAQData
  const sectionData = faq.sections.find(s => s.id === section)

  if (!sectionData) {
    return <Navigate to={localizeHref('/')} replace />
  }

  const question = sectionData.questions.find(q => q.slug === slug)

  if (!question) {
    return <Navigate to={localizeHref('/')} replace />
  }

  // Get section-specific keywords
  const keywords = section ? sectionKeywords[section] || [] : []

  return (
    <>
      <SEO
        title={question.title}
        description={question.description}
        section={section}
        sectionTitle={sectionData.title}
        slug={slug}
        keywords={keywords}
      />
      <DocPage
        title={question.title}
        description={question.description}
        keyTakeaways={question.keyTakeaways}
      >
        <ContentRenderer content={question.content} />
      </DocPage>
    </>
  )
}
